// HOOKS
import useColumns from "../../hooks/other/useColumns.other.hook";
import { useContext, useState, useEffect } from "react";
import useSelectAll from "../../hooks/other/useSelectAll.other.hook";
// CONTEXTS
import { UsersContext } from "../../contexts/users/users.context";
import { UserContext } from "../../contexts/user.context";
import { SelectedUsersContext } from "../../contexts/users/selectedUsers.users.context";
//COMPONENTS
import Refresh from "../../components/refresh/refresh.component";
import QueryContainer from "../../components/tables/query/queryContainer.table.component";
import PaginationBar from "../../components/tables/customizableTable/features/pagination/paginationBar";
import Spinner from "../../components/spinner/spinner.component";
import Table from "../../components/tables/customizableTable/table.customizableTable";
import ResizableHeader from "../../components/tables/customizableTable/headers/resizable.header";
import CreateUserModal from "./CreateUserModal";
import DefaultButton from "../../components/buttons/default.button";
import { ReactComponent as AddIcon } from "../../assets/icons/add.icon.svg";
import { useMutation } from "@apollo/client";
import { ACTIVATE_DEACTIVATE_USER } from "../../graphql/mutations/users/activateDeactivateUser.jsx";

const DEFAULT_USERS_COLUMNS = [
  {
    id: "name",
    label: "Name",
    editable: true,
    type: "text",
    width: "200",
    show: true,
    resizable: true,
  },
  {
    id: "email",
    label: "Email",
    editable: true,
    type: "text",
    width: "200",
    show: true,
    resizable: true,
  },
  {
    id: "role",
    label: "Role",
    editable: true,
    type: "text",
    width: "200",
    show: true,
    resizable: true,
  },
  {
    id: "actions",
    label: "Actions",
    editable: false,
    type: "actions",
    width: "150",
    show: true,
    resizable: false,
  },
];


const CLASSNAMES = {
  pagination: {
    arrow:
      "active:shadow-inner active:shadow-black bg-lightPrimary hover:bg-lightSecondary shadow shadow-black hover:cursor-pointer hover:dark:bg-darkSecondary dark:bg-darkPrimary dark:fill-white rounded-md w-8 h-8",
    currentPage:
      "shadow-inner shadow-black dark:bg-darkSecondary bg-lightSecondary dark:text-white text-white hover:cursor-pointer rounded h-8 w-8 flex items-center justify-center",
    page: "active:shadow-inner active:shadow-black shadow shadow-black hover:dark:bg-darkSecondary hover:bg-lightSecondary active:shadow-inner dark:text-white text-white dark:bg-darkPrimary bg-lightPrimary hover:cursor-pointer rounded h-8 w-8 flex items-center justify-center",
  },
  columnFilter: {
    dropdownButton: {
      dropdown:
        "border-animate rounded h-[250px] w-[250px] overflow-y-scroll absolute backdrop-blur-sm bg-greenGlass flex flex-col gap-1 p-2 top-11 z-50",
    },
  },
  limit: {
    dropdownButton: {
      dropdown:
        "border-animate rounded absolute backdrop-blur-sm bg-greenGlass flex flex-col gap-1 p-2 top-11 z-50",
    },
  },
  spinner: {
    container: "flex w-full h-full justify-center items-center",
    spinner:
      "shadow animate-spin border-dashed rounded-full h-16 w-16 border-2 border-primary",
  },
};
const filterHiddenColumns = (column) => column?.show;
const UsersPage = () => {
  const { user } = useContext(UserContext);
  const userIsAdmin = user?.role === "admin";

  const visibleColumns = DEFAULT_USERS_COLUMNS;

  const [activateDeactivateUser] = useMutation(ACTIVATE_DEACTIVATE_USER, {
    refetchQueries: ["GetUsers"],
  });

  const handleActivateDeactivate = async (id, isActive) => {
    const action = isActive ? "deactivate" : "activate";

    console.log("handleActivateDeactivate", id, isActive);

    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;

    try {
      await activateDeactivateUser({
        variables: { userId: id, active: !isActive }, 
      });
      fetchUsers();
    } catch (err) {
      alert(err?.graphQLErrors?.[0]?.message || err.message || "Unexpected error");
    }
  };


  const { selectAll, handleSelectAll, resetSelectAll } = useSelectAll();
  const { columns, setColumns, handleColumnFilterCheck, handleResize } =
    useColumns();
  const { selectedUsers, setSelectedUsers } = useContext(SelectedUsersContext);

  const {
    metaData,
    users,
    fetchUsers,
    isLoading,
    handleSearch,
    handleLimitChange,
    handlePageChange,
    handleSort,
    query,
  } = useContext(UsersContext);

  const { limit, page, search, sort } = query;
  const [showCreateModal, setShowCreateModal] = useState(false);
  // Reset selectAll when selectedOrderedParts is empty
  useEffect(() => {
    if (selectedUsers.length === 0) resetSelectAll();
  }, [selectedUsers]);
  // Reset selectAll when page changes
  useEffect(() => {
    resetSelectAll();
  }, [page]);

  useEffect(() => {
    setColumns(visibleColumns);
  }, []);

  useEffect(() => {
    const userIsAdmin = user?.role === "admin";

    const visibleColumns = DEFAULT_USERS_COLUMNS.filter(col => {
      if (col.id === "role" && !userIsAdmin) return false;
      if (col.id === "actions" && !userIsAdmin) return false;
      return true;
    });

    setColumns(visibleColumns);
  }, [user]);

  // Add/Remove orderedParts from selectedOrderedParts
  const selectAllHandler = () => {
    const updatedSelectedUsers = handleSelectAll(selectedUsers, users, "_id");
    setSelectedUsers(updatedSelectedUsers);
  };
  return (
    <div
      style={{ height: "calc(100vh - 136px)" }}
      className="p-2 flex flex-col gap-2 overflow-y-hidden"
    >
      <div className="flex flex-row justify-between">
        <QueryContainer
          queryProps={{
            search: {
              handleChange: handleSearch,
            },
            limit: {
              handleChange: handleLimitChange,
              limit: limit,
            },
            columnFilter: {
              handleChange: handleColumnFilterCheck,
              columns: columns,
            },
          }}
          classNames={{
            limit: CLASSNAMES.limit,
            columnFilter: CLASSNAMES.columnFilter,
          }}
        ></QueryContainer>
        {/* TO DO: Create Users Info Bar */}
        {userIsAdmin && (
          <div>
            <DefaultButton handleClick={() => setShowCreateModal(true)}>
              <AddIcon className="fill-white" />
              Add User
            </DefaultButton>
          </div>
        )}
      </div>
      <div className="p2">
        <PaginationBar
          classNames={CLASSNAMES.pagination}
          currentPage={page}
          totalPages={metaData.totalPages}
          handleChange={handlePageChange}
        />
      </div>
      <div className="flex flex-row justify-between">
        {/* TO DO: Create Users Info Bar */}
        <Refresh minimumValue={30} handleEvent={fetchUsers} />
      </div>
      <div className="overflow-x-auto h-full border bg-slate-100 rounded px-1 pb-8">
        {isLoading ? (
          <Spinner classNames={CLASSNAMES.spinner} />
        ) : (
          <Table
            header={
              <tr>
                <th className="max-w-[45px]">
                  <input
                    className="checked:accent-primary w-[45px]"
                    type="checkbox"
                    onChange={selectAllHandler}
                    checked={selectAll}
                  />
                </th>

                {columns?.filter(filterHiddenColumns)?.map((column) => (
                  <ResizableHeader
                    sortProps={{ sort, handleSort }}
                    key={column?.id}
                    column={column}
                    handleResize={handleResize}
                  >
                    {column?.label}
                  </ResizableHeader>
                ))}
              </tr>
            }
            body={users?.map((user, index) => (
              <tr key={index}>
                <td className="max-w-[45px]">
                  <input
                    className="checked:accent-primary w-[45px]"
                    type="checkbox"
                    onChange={() => {
                      const updatedSelectedUsers = handleSelectAll(
                        selectedUsers,
                        [user],
                        "_id"
                      );
                      setSelectedUsers(updatedSelectedUsers);
                    }}
                    checked={selectedUsers?.includes(user)}
                  />
                </td>
                {/* dynamic columns */}
                {columns
                  .filter((column) => column?.show)
                  .map((column) => {
                    // ✅ ACTION COLUMN
                    if (column.id === "actions") {
                      return (
                        <td key={column.id}>
                          <div className="flex gap-2">

                            {userIsAdmin && (
                              <button
                                onClick={() => handleActivateDeactivate(user._id, user.active === true)}
                                className={user.active === true ? "text-red-600 underline" : "text-green-600 underline"}
                              >
                                {user.active === true ? "Deactivate" : "Activate"}
                              </button>
                            )}

                          </div>
                        </td>
                      );
                    }

                    return <td key={column.id}>{user[column.id]}</td>;
                  })}
              </tr>
            ))}
          />
        )}
      </div>
      {userIsAdmin && showCreateModal && (
        <CreateUserModal
          onClose={() => {
            setShowCreateModal(false);
            fetchUsers();
          }}
        />
      )}
    </div>
  );
};

export default UsersPage;
